import Seo from "@/components/Seo";
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Loader2, Mail, ShieldCheck, Trash2, Database, Clock, CreditCard, CheckCircle2, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { COMPANY } from "@/data/legalContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  {
    icon: Mail,
    title: "1. Requesting account deletion",
    text: `To delete your Workpay account, please send an email to ${COMPANY.support} with the subject line "Account Deletion Request". Please include the email address associated with your Workpay account so we can verify your identity before processing the request.`,
  },
  {
    icon: Trash2,
    title: "2. Data that will be removed",
    text: "Once verified, we will delete or anonymize personal account information such as your name, email, profile data, and usage information related to your account.",
  },
  {
    icon: Database,
    title: "3. Data that may be retained",
    text: "Certain records may be retained where necessary for legal compliance, fraud prevention, dispute resolution, or financial record-keeping required by third-party service providers.",
  },
  {
    icon: CreditCard,
    title: "4. Payment information",
    text: "Workpay does not directly process payments. Payment information is handled by independent third-party payment providers and may be subject to their own data retention policies.",
  },
  {
    icon: Clock,
    title: "5. Processing time",
    text: "Verified deletion requests are typically processed within a reasonable timeframe. We may contact you if additional information is required.",
  },
];

const USER_TYPES = [
  { value: "vendor", label: "Vendor / Business" },
  { value: "customer", label: "Customer" },
  { value: "both", label: "Both" },
  { value: "other", label: "Other" },
];

const REQUEST_TYPES = [
  { value: "delete_account", label: "Delete my account" },
  { value: "delete_personal_data", label: "Delete my personal data" },
  { value: "delete_business_profile", label: "Delete my business profile" },
  { value: "copy_data", label: "Request a copy of my data" },
  { value: "other", label: "Other" },
];

const EMPTY = {
  full_name: "", email: "", phone: "", business_name: "",
  user_type: "", request_type: "", reason: "", message: "", consent: false,
};

export default function DeleteAccountData() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));
  const handleChange = (e) => setField(e.target.name, e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.user_type || !form.request_type) {
      toast.error("Please select user type and request type.");
      return;
    }
    if (!form.consent) {
      toast.error("Please confirm the consent checkbox to continue.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/deletion-request`, form);
      setDone(true);
      setForm(EMPTY);
      toast.success("Request received", { description: "We'll verify your identity and follow up by email." });
    } catch (err) {
      toast.error("Something went wrong", { description: "Please try again or email us directly." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="delete-account-data" className="pt-24">
      <Seo path="/delete-account-data" />
      {/* Header */}
      <section className="relative overflow-hidden bg-[#091630] py-16 sm:py-20">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-[#21c55e]/15 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-[#06b6d4]/15 blur-3xl" />
        <div className="absolute inset-0 dotted-bg opacity-[0.12]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold text-[#21c55e]">
            <ShieldCheck className="h-3.5 w-3.5" /> Privacy
          </span>
          <h1 className="font-display mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
            Delete Account &amp; Data
          </h1>
          <p className="mt-4 text-base text-slate-300 max-w-2xl leading-relaxed">
            Workpay allows users to request deletion of their account and associated personal data. This page
            explains how you can request deletion and what happens after your request is received.
          </p>
        </div>
      </section>

      <section className="relative py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1fr_minmax(360px,440px)] gap-10 items-start">
          {/* Steps */}
          <div className="space-y-4">
            {STEPS.map((s) => (
              <div key={s.title} data-testid={`delete-step-${s.title.charAt(0)}`} className="flex gap-4 rounded-3xl border border-slate-100 bg-white soft-shadow p-5 sm:p-6">
                <div className="h-11 w-11 rounded-2xl bg-[#ecfdf3] flex items-center justify-center shrink-0">
                  <s.icon className="h-5 w-5 text-[#16a34a]" />
                </div>
                <div>
                  <h3 className="font-display text-base font-bold text-[#091630] mb-1">{s.title}</h3>
                  <p className="text-[15px] text-slate-600 leading-relaxed">{s.text}</p>
                </div>
              </div>
            ))}

            <div className="rounded-3xl border border-[#21c55e]/25 bg-[#ecfdf3] p-6 flex gap-4">
              <Mail className="h-5 w-5 text-[#16a34a] shrink-0" />
              <div>
                <h3 className="font-display text-base font-bold text-[#091630] mb-1">6. Contact</h3>
                <p className="text-[15px] text-slate-600 leading-relaxed">
                  For questions regarding data deletion or privacy, contact{" "}
                  <a href={`mailto:${COMPANY.support}`} className="text-[#16a34a] font-semibold">{COMPANY.support}</a>.
                </p>
                <Link to="/privacy-policy" data-testid="delete-privacy-link" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-[#16a34a] hover:gap-2.5 transition-all">
                  Read our Privacy Policy <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          </div>

          {/* Online request form */}
          <div className="lg:sticky lg:top-28">
            {done ? (
              <div data-testid="delete-success" className="rounded-3xl border border-slate-100 bg-white soft-shadow p-8 text-center flex flex-col items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-[#ecfdf3] flex items-center justify-center">
                  <CheckCircle2 className="h-8 w-8 text-[#16a34a]" />
                </div>
                <h3 className="font-display text-2xl font-bold text-[#091630]">Request received</h3>
                <p className="text-slate-500 text-sm max-w-sm">
                  We've received your deletion request. To protect your account, we'll verify your identity and
                  follow up by email before processing it.
                </p>
                <Button variant="ghost" data-testid="delete-reset-button" onClick={() => setDone(false)} className="text-[#16a34a] hover:text-[#16a34a] hover:bg-[#ecfdf3]">
                  Submit another request
                </Button>
              </div>
            ) : (
              <form data-testid="delete-request-form" onSubmit={handleSubmit} className="rounded-3xl border border-slate-100 bg-white soft-shadow p-6 sm:p-7 space-y-4">
                <div>
                  <h3 className="font-display text-lg font-bold text-[#091630]">Submit a request online</h3>
                  <p className="text-sm text-slate-500 mt-1">Prefer not to email? Send your request below and we'll verify and follow up.</p>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="full_name" className="text-sm text-slate-600 font-semibold">Full name</Label>
                  <Input id="full_name" name="full_name" required value={form.full_name} onChange={handleChange} placeholder="Roshan Ali" data-testid="delete-field-full_name" className="bg-slate-50 border-slate-200 h-11 rounded-xl focus-visible:ring-[#21c55e] focus-visible:border-[#21c55e]" />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="email" className="text-sm text-slate-600 font-semibold">Account email</Label>
                  <Input id="email" name="email" type="email" required value={form.email} onChange={handleChange} placeholder="you@business.com" data-testid="delete-field-email" className="bg-slate-50 border-slate-200 h-11 rounded-xl focus-visible:ring-[#21c55e] focus-visible:border-[#21c55e]" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="phone" className="text-sm text-slate-600 font-semibold">Phone</Label>
                    <Input id="phone" name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="+971 50 000 0000" data-testid="delete-field-phone" className="bg-slate-50 border-slate-200 h-11 rounded-xl focus-visible:ring-[#21c55e] focus-visible:border-[#21c55e]" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="business_name" className="text-sm text-slate-600 font-semibold">Business</Label>
                    <Input id="business_name" name="business_name" value={form.business_name} onChange={handleChange} placeholder="Optional" data-testid="delete-field-business_name" className="bg-slate-50 border-slate-200 h-11 rounded-xl focus-visible:ring-[#21c55e] focus-visible:border-[#21c55e]" />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label className="text-sm text-slate-600 font-semibold">I am a</Label>
                  <Select value={form.user_type} onValueChange={(v) => setField("user_type", v)}>
                    <SelectTrigger data-testid="delete-field-user_type" className="bg-slate-50 border-slate-200 h-11 rounded-xl focus:ring-[#21c55e]">
                      <SelectValue placeholder="Select user type" />
                    </SelectTrigger>
                    <SelectContent>
                      {USER_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label className="text-sm text-slate-600 font-semibold">Request type</Label>
                  <Select value={form.request_type} onValueChange={(v) => setField("request_type", v)}>
                    <SelectTrigger data-testid="delete-field-request_type" className="bg-slate-50 border-slate-200 h-11 rounded-xl focus:ring-[#21c55e]">
                      <SelectValue placeholder="Select request type" />
                    </SelectTrigger>
                    <SelectContent>
                      {REQUEST_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="message" className="text-sm text-slate-600 font-semibold">Message (optional)</Label>
                  <Textarea id="message" name="message" value={form.message} onChange={handleChange} placeholder="Anything you'd like us to know" rows={3} data-testid="delete-field-message" className="bg-slate-50 border-slate-200 rounded-xl focus-visible:ring-[#21c55e] focus-visible:border-[#21c55e]" />
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox id="consent" checked={form.consent} onCheckedChange={(v) => setField("consent", !!v)} data-testid="delete-field-consent" className="mt-0.5 data-[state=checked]:bg-[#21c55e] data-[state=checked]:border-[#21c55e]" />
                  <Label htmlFor="consent" className="text-xs text-slate-500 font-normal leading-relaxed cursor-pointer">
                    I confirm this is my account and I understand some records may be retained where required for
                    legal, compliance, fraud-prevention, or financial record-keeping purposes.
                  </Label>
                </div>

                <Button type="submit" disabled={loading} data-testid="delete-submit-button" className="w-full py-3.5 h-auto bg-[#21c55e] text-[#091630] font-bold rounded-full hover:bg-[#16a34a] hover:text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-70">
                  {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Submitting…</> : <>Submit deletion request</>}
                </Button>
                <p className="text-xs text-slate-400 text-center">Identity verification may be required before processing.</p>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
